import {
  addDefaultProps,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  addWrapper,
  branch,
  flowMax,
  renderNothing,
} from 'ad-hok'
import { FC, ReactElement } from 'react'
import { createUseStyles } from 'react-jss'
import { typedAs } from 'shared/src/utils/typedAs'
import {
  addDebouncedCopy,
  addEffectOnMount,
  addPropTrackingRef,
} from 'ad-hok-utils'
import { addApi } from 'shared/src/api'
import {
  Brand,
  Label,
  SearchResult,
  SearchResultClick,
  UserStatusLevel,
  UserWithImages,
} from 'shared/src/api/types'
import { isEmpty } from 'lodash'
import { trackEvent, trackSeachResultClick } from 'shared/src/utils/analytics'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Modal } from '../Modal'
import { SearchField } from './SearchField'
import { makeBrandUrl, makeFluidCreatorUrl, makeLabelUrl } from 'utils/routes'
import leftChevron from '../../assets/menu-left-chevron.svg'
import searchBrandIcon from '../../assets/search-brand-icon.svg'
import { serifFontFamily } from 'utils/styles'
import { sansSerifFontFamily } from 'utils/styles'
import { Avatar } from '../Avatar'
import { LAYOUT_MAX_WIDTH } from 'utils/layout'
import { LinkOrButton } from 'components/LinkOrButton'
import { MODAL_Z_INDEX } from 'utils/zIndex'
import UserBadge from 'components/profile/UserBadge'

const SIDE_PADDING = 24

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: 'black',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: 9,
    height: '100dvh',
    width: `calc(min(${LAYOUT_MAX_WIDTH}px, 100%) - ${SIDE_PADDING}px * 2)`,
    maxWidth: LAYOUT_MAX_WIDTH,
    marginLeft: 'auto',
    marginRight: 'auto',
    zIndex: MODAL_Z_INDEX,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 32,
  },
  backButton: {
    border: 'none',
    background: 'none',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0 0 0',
  },
  closeButtonIcon: {
    color: '#90734F',
  },
  promptContainer: {
    marginTop: 132,
    marginLeft: 16,
    display: 'flex',
    flexDirection: 'column',
  },
  prompt: {
    maxWidth: 169,
    fontFamily: serifFontFamily,
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '140%',
    letterSpacing: '0.4px',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  createNewButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: 64,
    borderRadius: 2,
    backgroundColor: 'rgba(54, 52, 50, 0.02)',
    paddingLeft: 20,
    paddingRight: 24,
    marginBottom: 31,
    flexShrink: 0,
  },
  searchResultContainer: {
    backgroundColor: 'transparent',
    flexShrink: 0,
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
  },
  searchResultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: 24,
    overflow: 'scroll',
  },
  result: {
    height: 32,
    border: 'none',
    background: 'none',
    padding: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    color: 'rgba(244, 244, 244, 0.9)',
    fontFamily: sansSerifFontFamily,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '17px',
    textAlign: 'left',
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
})

interface ResultProps {
  image: ReactElement | null
  name: string
  statusLevel?: UserStatusLevel
  path: string
  analyticsInput: SearchResultClick
}

const Result: FC<ResultProps> = flowMax(
  addDisplayName('Result'),
  addDefaultProps({
    statusLevel: UserStatusLevel.Regular,
  }),
  addApi,
  addHandlers({
    onClick:
      ({ analyticsInput, api }) =>
      () => {
        const analyticsParams =
          analyticsInput.type === 'creator'
            ? { creator_id: analyticsInput.value }
            : analyticsInput.type === 'brand'
            ? { brand_id: analyticsInput.value }
            : { label_id: analyticsInput.value }
        trackEvent(
          {
            category: 'general',
            action: 'search_result_click',
          },
          analyticsParams
        )
        trackSeachResultClick(analyticsInput, api)
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ image, name, statusLevel, path, onClick, classes }) => (
    <LinkOrButton
      navigateTo={path}
      onClick={onClick}
      className={classes.result}
      showBackButton
    >
      {image}
      <div className={classes.nameContainer}>
        {name}
        <UserBadge statusLevel={statusLevel} />
      </div>
    </LinkOrButton>
  )
)

interface CreatorProps {
  creator: UserWithImages
  path: string
}

const CreatorResult: FC<CreatorProps> = flowMax(
  addDisplayName('CreatorResult'),
  ({ creator, path }) => (
    <Result
      image={creator.image && <Avatar creator={creator} height={32} />}
      name={creator.username ?? ''}
      statusLevel={creator.statusLevel}
      path={path}
      analyticsInput={{
        type: 'creator',
        value: creator.id,
      }}
    />
  )
)

interface BrandProps {
  brand: Brand
  path: string
}

const BrandResult: FC<BrandProps> = flowMax(
  addDisplayName('BrandResult'),
  ({ brand: { id, name }, path }) => (
    <Result
      image={<img src={searchBrandIcon} alt="" />}
      name={name}
      path={path}
      analyticsInput={{
        type: 'brand',
        value: id,
      }}
    />
  )
)

interface LabelProps {
  label: Label
  path: string
}

const LabelResult: FC<LabelProps> = flowMax(
  addDisplayName('LabelResult'),
  ({ label: { id, name }, path }) => (
    <Result
      image={<img src={searchBrandIcon} alt="" />}
      name={name}
      path={path}
      analyticsInput={{
        type: 'label',
        value: id,
      }}
    />
  )
)

interface SearchResultsProps {
  searchResults: SearchResult[]
  pathForSearchResult: (searchResult: SearchResult) => string
  onTouchStart: () => void
}

const SearchResults: FC<SearchResultsProps> = flowMax(
  addDisplayName('SearchResults'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ searchResults, pathForSearchResult, onTouchStart, classes }) => (
    <div onTouchStart={onTouchStart} className={classes.searchResultsContainer}>
      {searchResults.map((searchResult) => {
        const path = pathForSearchResult(searchResult)
        const key = `${searchResult.type}-${searchResult.value.id}`

        return searchResult.type === 'creator' ? (
          <CreatorResult creator={searchResult.value} path={path} key={key} />
        ) : searchResult.type === 'brand' ? (
          <BrandResult brand={searchResult.value} path={path} key={key} />
        ) : (
          <LabelResult label={searchResult.value} path={path} key={key} />
        )
      })}
    </div>
  )
)

interface Props {
  onClose: () => void
}

export const SearchModal: FC<Props> = flowMax(
  addDisplayName('SearchModal'),
  addState('searchText', 'onChangeSearchText', ''),
  addRef('inputRef', typedAs<HTMLInputElement | null>(null)),
  addHandlers(
    {
      focusInput:
        ({ inputRef }) =>
        () => {
          inputRef.current?.focus()
        },
      blurInputIfFocused:
        ({ inputRef }) =>
        () => {
          if (!inputRef.current) return
          if (window.document.activeElement !== inputRef.current) return
          inputRef.current.blur()
        },
    },
    ['inputRef']
  ),
  addEffectOnMount(({ focusInput }) => () => {
    focusInput()
  }),
  addHandlers({
    onClear:
      ({ onChangeSearchText, focusInput }) =>
      () => {
        onChangeSearchText('')
        focusInput()
      },
  }),
  addState(
    'searchResults',
    'setSearchResults',
    typedAs<SearchResult[] | null>(null)
  ),
  addDebouncedCopy(200, 'searchText', 'searchTextDebounced'),
  addApi,
  addPropTrackingRef('searchText', 'searchTextRef'),
  addState('isLoadingSearchResults', 'setIsLoadingSearchResults', false),
  addEffectOnMount(({ focusInput }) => () => {
    focusInput()
  }),
  addEffect(
    ({
        searchTextDebounced,
        api,
        searchTextRef,
        setSearchResults,
        setIsLoadingSearchResults,
      }) =>
      () => {
        setIsLoadingSearchResults(true)
        api.search.search(searchTextDebounced).then((results) => {
          setIsLoadingSearchResults(false)
          if (searchTextRef.current !== searchTextDebounced) return

          setSearchResults(results)
        })
      },
    ['searchTextDebounced']
  ),

  addHandlers({
    pathForSearchResult:
      () =>
      (searchResult: SearchResult): string => {
        if (searchResult.type === 'creator') {
          if (!searchResult.value.username) {
            console.error(
              'Trying to navigate to a creator with no username',
              searchResult.value
            )
            return '/'
          }

          return makeFluidCreatorUrl(searchResult.value.username)
        } else if (searchResult.type === 'brand') {
          return makeBrandUrl(searchResult.value.id)
        } else {
          return makeLabelUrl(searchResult.value.id)
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addWrapper(
    (
      render,
      { inputRef, onClose, searchText, onChangeSearchText, onClear, classes, t }
    ) => (
      <Modal containerClassName={classes.container}>
        <div className={classes.header}>
          <button onClick={onClose} className={classes.backButton}>
            <img src={leftChevron} alt={t('back')} />
          </button>
          <SearchField
            value={searchText}
            onChange={onChangeSearchText}
            onClear={onClear}
            inputRef={inputRef}
          />
        </div>
        {render()}
      </Modal>
    )
  ),
  branch(
    ({ searchResults, isLoadingSearchResults }) =>
      !searchResults || (isEmpty(searchResults) && isLoadingSearchResults),
    renderNothing()
  ),
  ({ searchResults, pathForSearchResult, blurInputIfFocused }) => (
    <SearchResults
      searchResults={searchResults!}
      pathForSearchResult={pathForSearchResult}
      onTouchStart={blurInputIfFocused}
    />
  )
)
